// The Media page (/media): press coverage of Mise and our own news posts.
//
// Items here ship with the site. Items added at /admin/media come from the API; the page shows both,
// newest first. To move an item into the admin instead, delete it here and add it there.
//
// kind "press": someone else's story about Mise. Needs outlet, url (the story), date, title (their headline,
//               word for word) and a one- or two-sentence summary in our own words.
// kind "post":  our own news. Gets its own page at /media/<slug>. See the post format notes in
//               docs/media-posts.md.
//
// date is YYYY-MM-DD. tag is one of: company, family-meals, heart-of-house, at-home.
// French fields (title_fr, summary_fr, body_fr, cta_label_fr) are optional; English shows when they're empty.

export const STATIC_MEDIA = [
  {
    id: "static-global-news-2026-11-03",
    kind: "press",
    format: "video",
    outlet: "Global News",
    date: "2026-11-03",
    title: "Calgary chef launches free platform to connect surplus with those in need",
    summary:
      "Global News Calgary on Mise: Family Meals, the free platform that lets restaurants, grocers and bakeries pass surplus food to food banks and community groups.",
    summary_fr:
      "Global News Calgary présente Mise : Family Meals, la plateforme gratuite qui permet aux restaurants, épiceries et boulangeries de remettre leurs surplus aux banques alimentaires et aux organismes communautaires.",
    url: "https://globalnews.ca/video/12134329/calgary-chef-launches-free-platform-to-connect-surplus-with-those-in-need/",
    tag: "family-meals",
  },
  {
    id: "static-calgary-herald-2026-10-27",
    kind: "press",
    format: "article",
    outlet: "Calgary Herald",
    author: "Mackenzie Rhode",
    date: "2026-10-27",
    title: "How to prevent food from going to waste: Calgary entrepreneurs connect businesses with non-profits",
    summary:
      "The Herald looks at two Calgary founders tackling food waste and food insecurity, including how Mise: Family Meals connects food businesses with non-profits.",
    summary_fr:
      "Le Herald présente deux fondateurs calgariens qui s'attaquent au gaspillage et à l'insécurité alimentaires, dont Mise : Family Meals, qui relie les commerces alimentaires aux organismes.",
    url: "https://calgaryherald.com/news/local-news/calgary-entrepreneurs-reducing-food-waste-supporting-non-profits",
    tag: "family-meals",
  },
  {
    id: "static-ctv-calgary-2026-10-23",
    kind: "press",
    format: "article",
    outlet: "CTV News Calgary",
    author: "Damien Wood",
    date: "2026-10-23",
    title: "New online platform helps move end-of-day food surplus between businesses and charities",
    summary:
      "CTV News Calgary on how founder and former chef Sean Burton built Mise: Family Meals to get end-of-day surplus to the organizations that feed people.",
    summary_fr:
      "CTV News Calgary raconte comment le fondateur et ancien chef Sean Burton a créé Mise : Family Meals pour acheminer les surplus de fin de journée vers les organismes qui nourrissent les gens.",
    url: "https://www.ctvnews.ca/calgary/article/new-online-platform-helps-move-end-of-day-food-surplus-between-businesses-and-charities/",
    tag: "family-meals",
  },
  {
    id: "static-post-family-meals-open",
    kind: "post",
    slug: "family-meals-is-open-in-calgary",
    date: "2026-11-03",
    tag: "family-meals",
    title: "Mise: Family Meals is open in Calgary",
    title_fr: "Mise : Family Meals est ouvert à Calgary",
    summary:
      "Our free food rescue network now connects Calgary bakeries, restaurants and grocers with the non-profits that feed people, so surplus food becomes tomorrow's meal, not waste.",
    summary_fr:
      "Notre réseau gratuit de récupération alimentaire relie maintenant les boulangeries, restaurants et épiciers de Calgary aux organismes qui nourrissent les gens, pour que les surplus deviennent le repas de demain plutôt que du gaspillage.",
    body: `Mise: Family Meals is now open to food businesses and non-profits across Calgary. It's free for both sides, and it always will be.

## How it works

- Restaurants, bakeries and grocers list what they can't use, with how it's been stored, a pickup window and allergen tags.
- Non-profits post what they need and when they can receive it, then claim listings or wait for partners to reach out.
- The Mise team reviews listings for food safety, helps partners find each other and shares the impact.

## Every drop-off counts

Each drop-off is weighed and confirmed in Mise, so there's a traceable record of what moved where. Those weights feed the live impact counters on our site: kilograms rescued, meals enabled and CO₂e avoided.

> If there's anything that we can do to help, why wouldn't we?
> — Sean Burton, founder, to CTV News

Thank you to CTV News Calgary, the Calgary Herald and Global News for helping us spread the word. If you run a kitchen with surplus, or you work with an organization that feeds people, we'd love to hear from you.`,
    body_fr: `Mise : Family Meals est maintenant ouvert aux commerces alimentaires et aux organismes de partout à Calgary. C'est gratuit pour tous, et ça le restera.

## Comment ça marche

- Les restaurants, boulangeries et épiciers publient ce qu'ils ne peuvent pas utiliser, avec le mode de conservation, une plage de cueillette et les allergènes.
- Les organismes indiquent leurs besoins et leurs heures de réception, puis réservent des offres ou attendent qu'un partenaire les contacte.
- L'équipe Mise vérifie la salubrité des offres, aide les partenaires à se trouver et fait connaître l'impact.

## Chaque livraison compte

Chaque livraison est pesée et confirmée dans Mise, ce qui laisse une trace de ce qui a été acheminé et où. Ces poids alimentent les compteurs d'impact en direct sur notre site : kilogrammes récupérés, repas rendus possibles et CO₂e évité.

> S'il y a quoi que ce soit qu'on peut faire pour aider, pourquoi ne pas le faire?
> — Sean Burton, fondateur, à CTV News

Merci à CTV News Calgary, au Calgary Herald et à Global News d'avoir fait connaître le projet. Si vous gérez une cuisine qui a des surplus, ou si vous travaillez pour un organisme qui nourrit les gens, écrivez-nous.`,
    url: "https://misefamilymeals.ca/register",
    cta_label: "Join the network",
    cta_label_fr: "Rejoindre le réseau",
  },
  {
    id: "static-post-heart-of-house-launch",
    kind: "post",
    slug: "heart-of-house-founding-pilots",
    date: "2026-11-01",
    tag: "heart-of-house",
    title: "Introducing Mise: Heart of House. We're looking for founding pilots",
    title_fr: "Voici Mise : Cœur de cuisine. Nous cherchons des pilotes fondateurs",
    summary:
      "Our back-of-house platform for independent restaurants is built, and we're choosing fifteen founding kitchens to run it with us first: Pro free for six months, then $199 a month for a year.",
    summary_fr:
      "Notre plateforme d'arrière-cuisine pour les restaurants indépendants est prête, et nous choisissons quinze cuisines fondatrices pour la faire tourner avec nous en premier : Pro gratuit pendant six mois, puis 199 $ par mois pendant un an.",
    image: "/images/media/heart-of-house-menu-engineering.png",
    image_alt:
      "The Heart of House Menu Engineering screen, shown with demonstration data: revenue, contribution margin and a portfolio health score of 87, with the menu split into stars, plowhorses, puzzles and dogs.",
    body: `Mise: Heart of House is our back-of-house platform for independent restaurants, and today we're opening applications for founding pilots.

Most owners find out what happened to their margin when the statement arrives, weeks after the month is over. By then the waste is in the bin, the price increase is on three invoices and the overtime is paid. Heart of House connects the systems you already use, from your POS and supplier invoices to inventory counts, recipes and labour, and shows you where the money went, what to fix first and whether the fix worked.

## What's inside

- Menu engineering that sorts every dish into stars, plowhorses, puzzles and dogs from your own sales and margins, like the screen above.
- True plate costs down to the ingredient, updated as supplier prices move.
- Waste logging, inventory counts and par lists that write themselves from your sales mix.
- Overtime alerts by shift and role, and a month-end P&L closeout.

## We need founding pilots

Heart of House is built and running today on demonstration kitchens. The founding pilots will be its first real kitchens, and that's where we need you. We're choosing fifteen restaurants, in three founding groups of five:

- Calgary full-service: independents and small groups, going live from January 2027.
- Quick-service: franchisees and independents, going live in spring 2027.
- Seasonal kitchens: open part of the year, going live for May 2027.

Founding kitchens get the full Pro plan free for six months, set up with you in person, then $199 a month for the next year instead of $299. There's no setup fee and no long contract. In return we ask for honest feedback, a short weekly check-in with Sean and permission to publish your results as a case study, under your name or anonymously.

> If Heart of House doesn't fit your kitchen, we'll say so.
> — Sean Burton, founder

If you run an independent kitchen, or you know someone who does, the application takes about two minutes. Sean reads every one himself.`,
    body_fr: `Mise : Cœur de cuisine est notre plateforme d'arrière-cuisine pour les restaurants indépendants, et nous ouvrons aujourd'hui les candidatures pour les pilotes fondateurs.

La plupart des propriétaires découvrent ce qui est arrivé à leur marge quand l'état financier arrive, des semaines après la fin du mois. Les pertes sont déjà à la poubelle, la hausse de prix est sur trois factures et les heures supplémentaires sont payées. Cœur de cuisine relie les systèmes que vous utilisez déjà, du point de vente aux factures des fournisseurs, en passant par les inventaires, les recettes et la main-d'œuvre, et vous montre où est passé l'argent, quoi corriger en premier et si la correction a fonctionné.

## Ce qu'il y a dedans

- L'ingénierie de menu, qui classe chaque plat en vedettes, chevaux de labour, énigmes et poids morts à partir de vos ventes et de vos marges, comme sur l'écran ci-dessus.
- Le coût réel de chaque assiette, jusqu'à l'ingrédient, mis à jour quand les prix des fournisseurs bougent.
- Le suivi des pertes, les inventaires et des listes de préparation qui se font seules à partir de vos ventes.
- Des alertes d'heures supplémentaires par quart et par poste, et une clôture mensuelle des résultats.

## Nous cherchons des pilotes fondateurs

Cœur de cuisine est prêt et fonctionne aujourd'hui dans des cuisines de démonstration. Les pilotes fondateurs seront ses premières vraies cuisines, et c'est là que nous avons besoin de vous. Nous choisissons quinze restaurants, en trois groupes fondateurs de cinq :

- Service complet à Calgary : indépendants et petits groupes, en service dès janvier 2027.
- Service rapide : franchisés et indépendants, en service au printemps 2027.
- Cuisines saisonnières : ouvertes une partie de l'année, en service pour mai 2027.

Les cuisines fondatrices obtiennent le forfait Pro complet gratuitement pendant six mois, configuré avec vous en personne, puis 199 $ par mois l'année suivante au lieu de 299 $. Aucuns frais d'installation, aucun long contrat. En retour, nous demandons des commentaires honnêtes, un court suivi hebdomadaire avec Sean et la permission de publier vos résultats en étude de cas, à votre nom ou de façon anonyme.

> Si Cœur de cuisine ne convient pas à votre cuisine, on vous le dira.
> — Sean Burton, fondateur

Si vous gérez une cuisine indépendante, ou si vous connaissez quelqu'un qui en gère une, la candidature prend environ deux minutes. Sean lit chacune d'elles lui-même.`,
    url: "/heart-of-house/pilot",
    cta_label: "Apply for a founding pilot place",
    cta_label_fr: "Postuler pour une place de pilote",
  },
];
